import { supabase } from '@/integrations/supabase/client';
import { Smartphone, BookOpen, Headphones } from 'lucide-react';

export type ReadingFormat = 'print' | 'digital' | 'audio';

export const READING_FORMAT_LABELS: Record<ReadingFormat, string> = {
  print: 'Print',
  digital: 'Digital/eBook',
  audio: 'Audiobook'
};

export const READING_FORMAT_ICONS = {
  print: BookOpen,
  digital: Smartphone,
  audio: Headphones
};

export const READING_FORMAT_EMOJI = {
  print: '📖',
  digital: '📱',
  audio: '🎧'
};

export async function getUserDefaultFormat(userId: string): Promise<ReadingFormat | null> {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('data')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching default format:', error);
      return null;
    }

    if (!data?.data || typeof data.data !== 'object' || Array.isArray(data.data)) {
      return null;
    }

    const preferences = data.data as Record<string, any>;
    return preferences.reading?.default_format || null;
  } catch (error) {
    console.error('Error in getUserDefaultFormat:', error);
    return null;
  }
}

export async function saveDefaultFormat(userId: string, format: ReadingFormat): Promise<void> {
  try {
    // Fetch existing preferences
    const { data: existingData } = await supabase
      .from('user_preferences')
      .select('data')
      .eq('user_id', userId)
      .maybeSingle();

    let currentData: Record<string, any> = {};
    if (existingData?.data && typeof existingData.data === 'object' && !Array.isArray(existingData.data)) {
      currentData = existingData.data as Record<string, any>;
    }
    
    // Merge with new reading format preference
    const updatedData = {
      ...currentData,
      reading: {
        ...(currentData.reading || {}),
        default_format: format
      }
    };

    // Upsert the preferences (conflict on user_id to keep single row per user)
    const { error } = await supabase
      .from('user_preferences')
      .upsert(
        {
          user_id: userId,
          data: updatedData as any,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id' }
      );

    if (error) {
      console.error('Error saving default format:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in saveDefaultFormat:', error);
    throw error;
  }
}
